import { assertNever } from "@redotech/util/type";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import { ReturnTypeEnum } from "../return";
import { SortDirection } from "../tables/table";
import {
  ArrayStringFilterBuilder,
  ArrayStringFilterDataSchema,
} from "./advanced-filters/array-string-filter";
import { BooleanFilterDataSchema } from "./advanced-filters/boolean-filter";
import {
  DateFilterBuilder,
  DateFilterDataSchema,
} from "./advanced-filters/date-filter";
import { ExistsFilterDataSchema } from "./advanced-filters/exists-filter";
import {
  NumberFilterBuilder,
  NumberFilterDataSchema,
} from "./advanced-filters/number-filter";
import {
  StringFilterBuilder,
  StringFilterDataSchema,
} from "./advanced-filters/string-filter";

export const DEFAULT_PAGE_SIZE = 50;

export const mapReturnTypeToViewType = (returnType: ReturnTypeEnum) => {
  switch (returnType) {
    case ReturnTypeEnum.RETURN:
      return ViewTypesNames.RETURNS;
    case ReturnTypeEnum.CLAIM:
      return ViewTypesNames.CLAIMS;
    case ReturnTypeEnum.WARRANTY:
      return ViewTypesNames.WARRANTIES;
    case ReturnTypeEnum.RECYCLE:
      return ViewTypesNames.RECYCLE;
    default:
      assertNever(returnType);
  }
};

// Valid page sizes allowed by table2 component
export const VALID_PAGE_SIZES = [
  10, 25, 50, 100, 150, 200, 250, 500, 1000, 1500,
] as const;
export type ValidPageSize = (typeof VALID_PAGE_SIZES)[number];

export const ViewSortSchema = z.object({
  direction: createEnum(SortDirection, { enumName: "SortDirection" }),
  key: z.string(),
});
export type ViewSort = z.infer<typeof ViewSortSchema>;

export const ViewColumnSchema = z.object({
  key: z.string(),
  width: z.number(),
  hidden: z.boolean(),
});
export type ViewColumn = z.infer<typeof ViewColumnSchema>;

const AdvancedFilterDataSchema_ = z.discriminatedUnion("type", [
  DateFilterDataSchema,
  ArrayStringFilterDataSchema,
  NumberFilterDataSchema,
  StringFilterDataSchema,
  ExistsFilterDataSchema,
  BooleanFilterDataSchema,
]);
export const AdvancedFilterDataSchema: z.ZodType<AdvancedFilterData> =
  AdvancedFilterDataSchema_;
export type AdvancedFilterData = {} & z.infer<typeof AdvancedFilterDataSchema_>;

const BaseViewSchema_ = z.object({
  _id: z.string().nullish(),
  table: z.string(),
  name: z.string(),
  filters: z.array(AdvancedFilterDataSchema),
  columns: z.array(ViewColumnSchema),
  sort: ViewSortSchema.nullish(),
  pageSize: z.number().nullish(),
  folderId: z.string().nullish(),
  position: z.number(),
  deleted: z.boolean().nullish(),
  isNotEditable: z.boolean().optional(),
});
export const BaseViewSchema: z.ZodType<BaseView> = BaseViewSchema_;
export interface BaseView extends z.infer<typeof BaseViewSchema_> {}

const BaseViewWithNotificationsSchema_ = BaseViewSchema_.extend({
  showNotification: z.boolean().default(false),
  hideWhenEmpty: z.boolean().optional(),
});
export const BaseViewWithNotificationsSchema: z.ZodType<BaseViewWithNotifications> =
  BaseViewWithNotificationsSchema_;
export interface BaseViewWithNotifications
  extends z.infer<typeof BaseViewWithNotificationsSchema_> {}

// Map view types to a Mongoose model and a Typescript interface
export const viewSchemas = {
  "fulfillment-groups": BaseViewSchema,
  "bulk-fulfillments": BaseViewSchema,
  "pick-session": BaseViewSchema,
  returns: BaseViewWithNotificationsSchema,
  claims: BaseViewWithNotificationsSchema,
  warranties: BaseViewWithNotificationsSchema,
  recycle: BaseViewWithNotificationsSchema,
  registrations: BaseViewSchema,
  "purchase-orders": BaseViewSchema,
  shipments: BaseViewSchema,
};

export enum ViewTypesNames {
  FULFILLMENT_GROUPS = "fulfillment-groups",
  BULK_FULFILLMENTS = "bulk-fulfillments",
  PICK_SESSION = "pick-session",
  RETURNS = "returns",
  CLAIMS = "claims",
  WARRANTIES = "warranties",
  RECYCLE = "recycle",
  REGISTRATIONS = "registrations",
  PURCHASE_ORDERS = "purchase-orders",
  SHIPMENTS = "shipments",
}

export type viewTypes = {
  [key in keyof typeof viewSchemas]: z.infer<(typeof viewSchemas)[key]>;
};

export type FilterBuilder =
  | typeof DateFilterBuilder
  | typeof ArrayStringFilterBuilder
  | typeof NumberFilterBuilder
  | typeof StringFilterBuilder;

const baseViewWithIdSchema_ = BaseViewSchema_.extend({ _id: z.string() });
const baseViewWithIdSchema: z.ZodType<BaseViewWithId> = baseViewWithIdSchema_;
export interface BaseViewWithId extends z.infer<typeof baseViewWithIdSchema_> {}

const baseViewWithNotificationsAndIdSchema_ =
  BaseViewWithNotificationsSchema_.extend({ _id: z.string() });
export const baseViewWithNotificationsAndIdSchema: z.ZodType<BaseViewWithNotificationsAndId> =
  baseViewWithNotificationsAndIdSchema_;
export interface BaseViewWithNotificationsAndId
  extends z.infer<typeof baseViewWithNotificationsAndIdSchema_> {}

export const viewWithIdSchemas = {
  "fulfillment-groups": baseViewWithIdSchema,
  "bulk-fulfillments": baseViewWithIdSchema,
  "pick-session": baseViewWithIdSchema,
  returns: baseViewWithNotificationsAndIdSchema,
  claims: baseViewWithNotificationsAndIdSchema,
  warranties: baseViewWithNotificationsAndIdSchema,
  recycle: baseViewWithNotificationsAndIdSchema,
  registrations: baseViewWithIdSchema,
  "purchase-orders": baseViewWithIdSchema,
  shipments: baseViewWithIdSchema,
};

export type viewWithIdTypes = {
  [key in keyof typeof viewWithIdSchemas]: z.infer<
    (typeof viewWithIdSchemas)[key]
  >;
};

export type FulfillmentGroupView =
  viewWithIdTypes[ViewTypesNames.FULFILLMENT_GROUPS];

export type BulkFulfillmentView =
  viewWithIdTypes[ViewTypesNames.BULK_FULFILLMENTS];

export type PickSessionView = viewWithIdTypes[ViewTypesNames.PICK_SESSION];
export const PickSessionViewSchema =
  viewWithIdSchemas[ViewTypesNames.PICK_SESSION];

export type ReturnView = viewWithIdTypes[ViewTypesNames.RETURNS];
export type ClaimView = viewWithIdTypes[ViewTypesNames.CLAIMS];
export type WarrantyView = viewWithIdTypes[ViewTypesNames.WARRANTIES];
export type RecycleView = viewWithIdTypes[ViewTypesNames.RECYCLE];
export type RegistrationView = viewWithIdTypes[ViewTypesNames.REGISTRATIONS];
export type PurchaseOrderView = viewWithIdTypes[ViewTypesNames.PURCHASE_ORDERS];
export type ShipmentView = viewWithIdTypes[ViewTypesNames.SHIPMENTS];

export const ClientFulfillmentGroupView =
  viewWithIdSchemas["fulfillment-groups"];
export type ClientFulfillmentGroupView = z.infer<
  typeof ClientFulfillmentGroupView
>;

export const ClientBulkFulfillmentView = viewWithIdSchemas["bulk-fulfillments"];
export type ClientBulkFulfillmentView = z.infer<
  typeof ClientBulkFulfillmentView
>;

export const ClientReturnView = viewWithIdSchemas.returns;
export type ClientReturnView = z.infer<typeof ClientReturnView>;
export const ClientClaimView = viewWithIdSchemas.claims;
export type ClientClaimView = z.infer<typeof ClientClaimView>;
export const ClientWarrantyView = viewWithIdSchemas.warranties;
export type ClientWarrantyView = z.infer<typeof ClientWarrantyView>;
export const ClientRecycleView = viewWithIdSchemas.recycle;
export type ClientRecycleView = z.infer<typeof ClientRecycleView>;
export const ClientRegistrationView = viewWithIdSchemas.registrations;
export type ClientRegistrationView = z.infer<typeof ClientRegistrationView>;
export const ClientPurchaseOrdersView = viewWithIdSchemas["purchase-orders"];
export type ClientPurchaseOrdersView = z.infer<typeof ClientPurchaseOrdersView>;
export const ClientShipmentView = viewWithIdSchemas.shipments;
export type ClientShipmentView = z.infer<typeof ClientShipmentView>;
